// The live BioTime connector, as HR sees it: is attendance current, and — for a
// connector installed but not yet enrolled — the go-live date and terminal
// timezone that switch it on.
//
// ⚠️ A connector fails quietly. The BioTime computer is switched off, the
// terminals keep recording, and every other screen goes on showing yesterday as
// though it were today. This card is the one place that says so, which is why
// "could not ask" renders as unknown (amber), never as fine.
//
// Totals are NOT reconciled from here: comparing counts needs BioTime's own
// numbers, which only the on-premises agent can read. The card says that rather
// than offering a button that could only ever pretend.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Download, KeyRound, Loader2, PlugZap, RefreshCw, UserCheck } from 'lucide-react';
import { IdentityMatcher } from '@/features/pages/migration/AttendanceSourcePanel';
import { identityApi } from '@/services/migrationService';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatDate } from '@/lib/orgFormat';
import { cn } from '@/lib/utils';
import {
  createInstallerCode, enrolBioTime, getPolicy, listBioTimeConnectors,
  type AttendancePolicy, type BioTimeConnector, type PairingCode,
} from '@/services/attendanceService';
import {
  checkEnrolment, describeHealth, installerFileName, installerPath, isWindowsClient, pickInstallerArch, terminalTimezoneOptions, unknownHealth,
  type InstallerArch,
  type HealthView, type Tone,
} from './connectorHealth';

const TONE: Record<Tone, string> = {
  good: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-muted text-muted-foreground',
};

// ⚠️ A unique URL per click. Cloudflare treats .exe as a cacheable type and its
// zone browser-cache TTL (4 hours) overrides our no-cache header, and its edge
// keeps a copy too — so a fixed URL could hand out yesterday's agent after a
// fix ships. The query string is part of both cache keys.
const freshInstallerUrl = (arch: InstallerArch) => `${installerPath(arch)}?v=${Date.now()}`;

/** This computer's Windows, from client hints where the browser has them. */
async function detectInstallerArch(): Promise<InstallerArch> {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string; getHighEntropyValues?: (h: string[]) => Promise<Record<string, string>> };
  };
  let hints: Record<string, string> = {};
  try {
    hints = (await nav.userAgentData?.getHighEntropyValues?.(['bitness', 'architecture'])) ?? {};
  } catch { /* hints refused: fall back to the user-agent string */ }
  return pickInstallerArch({
    platform: nav.userAgentData?.platform, bitness: hints.bitness,
    architecture: hints.architecture, userAgent: navigator.userAgent,
  });
}

/** True only when the real program is there — not the app's fallback page. */
async function installerAvailable(arch: InstallerArch): Promise<boolean> {
  try {
    const res = await fetch(freshInstallerUrl(arch), { method: 'HEAD', cache: 'no-store' });
    const type = (res.headers.get('content-type') || '').toLowerCase();
    return res.ok && !type.includes('text/html');
  } catch {
    return false;
  }
}

function triggerDownload(arch: InstallerArch, fileName: string) {
  const a = document.createElement('a');
  a.href = freshInstallerUrl(arch);
  a.download = fileName; // same-origin: the browser saves it under this name
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const HealthBanner: React.FC<{ view: HealthView }> = ({ view }) => (
  <div className={cn('flex items-start gap-3 rounded-xl px-4 py-3', TONE[view.tone])} data-testid="connector-health">
    {view.tone === 'good'
      ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />}
    <div className="space-y-1">
      <p className="text-sm font-semibold">{view.headline}</p>
      <p className="text-sm text-foreground">{view.detail}</p>
      {view.action && <p className="text-sm font-medium text-foreground">{view.action}</p>}
    </div>
  </div>
);

const EnrolForm: React.FC<{ conn: BioTimeConnector; onDone: () => void }> = ({ conn, onDone }) => {
  const { t } = useTranslation('attendance');
  const [timezone, setTimezone] = useState('');
  const tzOptions = React.useMemo(() => terminalTimezoneOptions(), []);
  const [goLive, setGoLive] = useState('');
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const check = checkEnrolment({ timezone, goLive, today: localToday(), retroactiveAcknowledged: ack });

  const submit = async () => {
    if (check.blockers.length) return;
    setSaving(true);
    try {
      const res = await enrolBioTime({ connection_id: conn.connection_id, timezone, measurement_start_date: goLive });
      if (res.warning) {
        // Bound, but the boundary did not save — loud, not a success toast.
        toast.error(res.warning, { duration: 10000 });
      } else {
        toast.success(t('connector_enrolled', 'Connector enrolled. Measurement starts {{date}}.', { date: formatDate(goLive) }));
      }
      onDone();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('connector_enrol_failed', 'Could not enrol the connector.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('connector_enrol_intro', '"{{name}}" is installed but not switched on yet. Two facts about your site switch it on.', { name: conn.name })}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="conn-tz">{t('connector_tz', 'Timezone of the fingerprint terminals')}</Label>
          <select
            id="conn-tz"
            className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-charcoal"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            <option value="">{t('connector_tz_choose', 'Choose a timezone…')}</option>
            {tzOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">
            {t('connector_tz_hint', 'BioTime records local clock times with no timezone. A wrong one moves punches near midnight onto the wrong day.')}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="conn-golive">{t('connector_golive', 'Go-live date')}</Label>
          <Input id="conn-golive" type="date" value={goLive} onChange={(e) => { setGoLive(e.target.value); setAck(false); }} />
          <p className="text-xs text-muted-foreground">
            {t('connector_golive_hint', 'The day staff were told attendance is measured. Before it, nobody is marked late, no manager is notified and no HR action opens.')}
          </p>
        </div>
      </div>
      {check.retroactive && (
        <label className="flex items-start gap-2 rounded-xl bg-warning-soft px-4 py-3 text-sm">
          <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          <span>
            {t('connector_golive_past', 'This date is in the past. Days from {{date}} until today will be measured — lateness counted, and HR actions possible — for days nobody may have been told about. I mean this date.', { date: formatDate(goLive) })}
          </span>
        </label>
      )}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {check.blockers.length > 0 && (
          <p className="text-xs text-muted-foreground">{check.blockers[0]}</p>
        )}
        <Button onClick={submit} disabled={saving || check.blockers.length > 0} className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
          {t('connector_enrol', 'Switch on')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Installing the agent on the BioTime computer.
 *
 * Primary path: "Download installer" — done ON the BioTime computer. It creates
 * a one-time code and saves the agent as "KaziPeople-BioTime-Installer-<code>.exe";
 * the program reads the code from its own file name, so double-clicking it asks
 * only for the BioTime password. The code is never put inside the file, and it
 * still works once, for one hour. Secondary path: the code alone, for the
 * PowerShell installer or a renamed file.
 */
const InstallerCode: React.FC<{ connectionId: string }> = ({ connectionId }) => {
  const { t } = useTranslation('attendance');
  const [code, setCode] = useState<PairingCode | null>(null);
  const [downloaded, setDownloaded] = useState<InstallerArch | null>(null);
  const [busy, setBusy] = useState<'download' | 'code' | null>(null);

  const create = async (mode: 'download' | 'code') => {
    setBusy(mode);
    try {
      const arch = mode === 'download' ? await detectInstallerArch() : 'x86';
      if (mode === 'download' && !(await installerAvailable(arch))) {
        // Never mint a code for a download that would save a web page as .exe.
        toast.error(t('connector_download_missing', 'The installer is not available right now. Use "Show code only" with the PowerShell installer, or try again later.'));
        return;
      }
      const c = await createInstallerCode(connectionId);
      setCode(c);
      if (mode === 'download') {
        triggerDownload(arch, installerFileName(c.code));
        setDownloaded(arch);
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('connector_code_failed', 'Could not create an installer code.')));
    } finally {
      setBusy(null);
    }
  };

  const onWindows = React.useMemo(() => isWindowsClient({
    platform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform,
    userAgent: navigator.userAgent,
  }), []);
  const until = code ? new Date(code.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="space-y-3 rounded-xl border border-border px-4 py-3" data-testid="installer-code">
      {!code && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {t('connector_download_intro', 'On the BioTime computer (Windows): download the installer and double-click it. It asks only for the BioTime password.')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => create('download')} disabled={busy !== null} className="gap-1.5">
              {busy === 'download' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t('connector_download', 'Download installer')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => create('code')} disabled={busy !== null} className="gap-1.5">
              {busy === 'code' ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {t('connector_code_create', 'Show code only')}
            </Button>
          </div>
        </div>
      )}
      {code && downloaded && (
        <div className="space-y-1.5 text-sm">
          <p className="font-medium text-charcoal">
            {downloaded === 'x64'
              ? t('connector_download_done_x64', 'Installer downloaded (64-bit Windows). Now, on this computer:')
              : t('connector_download_done_x86', 'Installer downloaded (32-bit — works on any Windows). Now, on this computer:')}
          </p>
          {!onWindows && (
            <p className="flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2 text-sm text-foreground" data-testid="not-windows-note">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <span>
                {t('connector_download_not_windows', "This computer isn't Windows, so the installer will not open here. Run it on the BioTime computer: copy this file there (for example on a USB stick), or click Download installer on that computer. The code inside the file name works once, until {{time}}.", { time: until })}
              </span>
            </p>
          )}
          <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
            <li>{onWindows
              ? t('connector_download_step1', 'Open your Downloads folder and double-click the KaziPeople BioTime installer.')
              : t('connector_download_step1_copy', 'On the BioTime computer, double-click the KaziPeople BioTime installer.')}</li>
            <li>{t('connector_download_step2', 'If Windows says "Windows protected your PC", click More info, then Run anyway. Allow it to make changes.')}</li>
            <li>{t('connector_download_step3', 'Confirm the BioTime address and type the BioTime password. The installer does the rest.')}</li>
          </ol>
          <p className="text-xs text-muted-foreground">
            {t('connector_download_other', 'Need the other version? Same code:')}{' '}
            <button type="button" className="underline" onClick={() => triggerDownload('x64', installerFileName(code.code))}>
              {t('connector_download_x64', '64-bit')}
            </button>
            {' · '}
            <button type="button" className="underline" onClick={() => triggerDownload('x86', installerFileName(code.code))}>
              {t('connector_download_x86', '32-bit')}
            </button>
          </p>
        </div>
      )}
      {code && (
        <div>
          <p className="text-xs text-muted-foreground">{t('connector_code_label', 'Installer code')}</p>
          <p className="font-mono text-2xl font-semibold tracking-widest text-charcoal select-all">{code.code}</p>
          <p className="text-xs text-muted-foreground">
            {downloaded
              ? t('connector_code_hint_download', 'Already inside the download\'s name; type it only if the installer asks. Works once, until {{time}}. Downloading again switches off the previous agent.', { time: until })
              : t('connector_code_hint', 'Works once, until {{time}}. Read it out on the install call — do not email it. Installing with a new code switches off the previous agent.', { time: until })}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Which BioTime code is which employee — the SAME matching screen as the
 * attendance file import (IdentityMatcher), pointed at this connector. Codes
 * already matched for the file import are matched here; confirming here also
 * matches the file import; punches that arrived before a code was matched are
 * counted for the person once it is.
 */
const WhoIsWho: React.FC<{ connectionId: string; unmatched: number; onChanged: () => void; readOnly?: boolean }> = ({
  connectionId, unmatched, onChanged, readOnly = false,
}) => {
  const { t } = useTranslation('attendance');
  const [open, setOpen] = useState(unmatched > 0);
  const api = React.useMemo(() => {
    const base = identityApi(`/api/v1/integrations/biotime/connectors/${connectionId}`);
    // Refresh the card's counts after every change made on the matcher.
    const after = <T,>(p: Promise<T>) => p.then((v) => { onChanged(); return v; });
    return {
      ...base,
      confirm: (...a: Parameters<typeof base.confirm>) => after(base.confirm(...a)),
      dismiss: (...a: Parameters<typeof base.dismiss>) => after(base.dismiss(...a)),
      undismiss: (...a: Parameters<typeof base.undismiss>) => after(base.undismiss(...a)),
      forget: (...a: Parameters<typeof base.forget>) => after(base.forget(...a)),
    };
  }, [connectionId, onChanged]);

  return (
    <div className="rounded-xl border border-border" data-testid="who-is-who">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-charcoal">
          <UserCheck className="h-4 w-4" aria-hidden />
          {t('connector_who_title', 'Who is who — BioTime codes and employees')}
        </span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {unmatched > 0
            ? <span className="rounded bg-warning-soft px-2 py-0.5 text-warning">{t('connector_who_unmatched', '{{n}} punches with no employee', { n: unmatched })}</span>
            : t('connector_who_ok', 'every punch so far has an employee')}
          {open ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
        </span>
      </button>
      {open && (
        <div className="border-t border-border p-2">
          <IdentityMatcher api={api} canWrite={!readOnly} context="live" />
        </div>
      )}
    </div>
  );
};

const ConnectorBody: React.FC<{ conn: BioTimeConnector; policy: AttendancePolicy | null; onChanged: () => void; readOnly?: boolean }> = ({ conn, policy, onChanged, readOnly = false }) => {
  const { t } = useTranslation('attendance');
  if (!conn.enrolled) {
    return readOnly
      ? <p className="text-sm text-muted-foreground">{t('connector_not_enrolled_ro', 'This connector has not been switched on yet. HR switches it on.')}</p>
      : <EnrolForm conn={conn} onDone={onChanged} />;
  }

  const view = conn.health ? describeHealth(conn.health) : unknownHealth();
  const h = conn.health;
  const facts: Array<[string, string]> = [
    [t('connector_fact_golive', 'Measured from'), policy?.measurement_start_date ? formatDate(policy.measurement_start_date) : t('connector_fact_none', 'not set')],
    [t('connector_fact_tz', 'Terminal timezone'), conn.timezone || '—'],
  ];
  if (h) {
    // Two different facts: whether the agent is ALIVE, and when attendance last
    // ARRIVED. At night the first stays fresh while the second ages, and that
    // is normal.
    facts.push([t('connector_fact_seen', 'Agent last seen'), h.last_seen_at ? new Date(h.last_seen_at).toLocaleString() : t('connector_fact_never', 'never')]);
    facts.push([t('connector_fact_last', 'Last punch received'), h.last_acknowledged_at ? new Date(h.last_acknowledged_at).toLocaleString() : t('connector_fact_never', 'never')]);
    facts.push([t('connector_fact_total', 'Punches received'), String(h.total_accepted ?? 0)]);
    facts.push([t('connector_fact_unmatched', 'Punches with no employee'), String(h.unmatched_punches ?? 0)]);
    if (h.history?.oldest_punch_at && h.history?.newest_punch_at) {
      facts.push([t('connector_fact_history', 'History held'),
        `${formatDate(h.history.oldest_punch_at)} → ${formatDate(h.history.newest_punch_at)}`]);
    }
    facts.push([t('connector_fact_backfill', 'History import'), !h.backfill
      ? t('connector_fact_backfill_none', 'not run yet')
      : h.backfill.completed_at
        ? t('connector_fact_backfill_done', 'complete — {{n}} punches imported', { n: h.backfill.accepted ?? 0 })
        : t('connector_fact_backfill_n', '{{n}} punches imported', { n: h.backfill.accepted ?? 0 })]);
    // "Current" alone only ever meant "recent punches arrived". The daily
    // check is the agent's own count from BioTime, day by day.
    if (h.reconciliation) {
      const short = h.reconciliation.days_short;
      facts.push([t('connector_fact_daily_check', 'Daily check against BioTime'), short.length === 0
        ? t('connector_fact_daily_ok', 'last {{n}} days match', { n: h.reconciliation.days_checked })
        : t('connector_fact_daily_short', '{{n}} day(s) short: {{days}}', {
          n: short.length, days: short.map((d) => formatDate(d.date)).join(', '),
        })]);
    }
    if ((h.rejected_punches ?? 0) > 0) {
      facts.push([t('connector_fact_rejected', 'Punches that could not be accepted'), String(h.rejected_punches)]);
    }
  }
  return (
    <div className="space-y-4">
      <HealthBanner view={view} />
      {(h?.alerts?.length ?? 0) > 0 && (
        <ul className="space-y-1 rounded-md border border-border p-3 text-sm" aria-label={t('connector_alerts', 'Open connector alerts')}>
          {h!.alerts!.map((a) => (
            <li key={a.kind}>
              <span className="font-medium text-charcoal">{alertLabel(a.kind, t)}</span>
              <span className="text-muted-foreground"> · {t('connector_alert_since', 'since {{when}}', { when: new Date(a.opened_at).toLocaleString() })}</span>
            </li>
          ))}
        </ul>
      )}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {facts.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-border py-1.5">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-medium text-charcoal">{v}</dd>
          </div>
        ))}
      </dl>
      {policy && (policy.overnight_mode ?? 'split') === 'split' && (
        <p className="text-xs text-muted-foreground">
          {t('connector_overnight_hint', 'Night shifts are counted as two separate days. If staff work past midnight, choose how to count them on the Policy tab.')}
        </p>
      )}
      <WhoIsWho connectionId={conn.connection_id} unmatched={h?.unmatched_punches ?? 0} onChanged={onChanged} readOnly={readOnly} />
      {/* Installer codes pair a new agent — a change, so not for read-only. */}
      {!readOnly && <InstallerCode connectionId={conn.connection_id} />}
      {!h?.reconciliation && (
        <p className="text-xs text-muted-foreground">
          {t('connector_reconcile_note', 'The agent has not reported BioTime\'s daily totals yet. Until it does, "current" means recent punches arrived — not that none are missing.')}
        </p>
      )}
    </div>
  );
};

function alertLabel(kind: string, t: (k: string, d: string) => string): string {
  if (kind === 'offline') return t('connector_alert_offline', 'Connector offline — a missing scan is not an absence');
  if (kind === 'backfill_stalled') return t('connector_alert_backfill', 'History import has stopped');
  if (kind === 'codes_waiting') return t('connector_alert_codes', 'Device codes waiting to be matched');
  if (kind === 'counts_short') return t('connector_alert_counts', 'Some days hold fewer punches than BioTime');
  return kind;
}

/** Renders nothing for a tenant with no BioTime connection installed. */
/** readOnly: the connector-diagnostics view (IT) — health, progress and who
 *  still needs matching, with every action hidden. The server refuses changes
 *  to that grant anyway; this keeps the screen honest about it. */
export const LiveConnectorCard: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const { t } = useTranslation('attendance');
  const [conns, setConns] = useState<BioTimeConnector[] | null>(null);
  const [policy, setPolicy] = useState<AttendancePolicy | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listBioTimeConnectors(), getPolicy().catch(() => null)])
      .then(([c, p]) => { setConns(c); setPolicy(p); setFailed(false); })
      .catch((e) => {
        // 403/404: this person cannot administer the connector, or there is
        // none to administer — nothing to show. Anything else is "could not
        // ask", which is said out loud rather than read as "no connector".
        const code = e?.response?.status;
        if (code === 403 || code === 404) { setConns([]); setFailed(false); } else { setFailed(true); }
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Nothing until the first answer: most tenants have no connector, and a
  // skeleton that appears and vanishes on every visit is noise.
  if (conns === null && !failed) return null;
  if (!failed && (conns?.length ?? 0) === 0) return null;

  return (
    <Card data-testid="live-connector-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">{t('connector_title', 'Live attendance — BioTime')}</CardTitle>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          {t('connector_refresh', 'Refresh')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {failed ? (
          <HealthBanner view={unknownHealth()} />
        ) : (
          conns!.map((c) => (
            <div key={c.connection_id} className="space-y-3">
              {conns!.length > 1 && <p className="text-sm font-semibold text-charcoal">{c.name}</p>}
              <ConnectorBody conn={c} policy={policy} onChanged={load} readOnly={readOnly} />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

